import { Tag } from 'antd';
import Confirm from 'components/Confirm';
import LinkRelationModal from 'components/Modals/LinkRelationModal';
import RelationsTable from 'components/RelationsTable';
import useEntities from 'hooks/useEntities';
import { useMemo, useState } from 'react';
import useLoginStore from 'store/loginStore';
import icons from 'utils/icons';
import { getStatusText } from 'utils/status';

const UserPlayers = ({ sourceEntityId, data, onChange }) => {
    const entities = useEntities();
    const isSystemAdmin = useLoginStore(s => s.isSystemAdmin)();
    const sourceEntity = entities.User;
    const targetEntity = entities.Player;
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const [linkRelation, setLinkRelation] = useState(null);

    const hasExistingPlayer = data && data.length > 0;

    const linkRelationOk = async (values) => {
        await sourceEntity.linkWith(sourceEntityId, targetEntity, values);
        setLinkRelation(null);
        onChange();
    };

    const linkRelationCancel = () => {
        setLinkRelation(null);
    };

    const handleAddClick = () => {
        if (hasExistingPlayer) {
            setShowLimitDialog(true);
        } else {
            setLinkRelation(targetEntity.name);
        }
    };

    const handleLimitDialogConfirm = () => {
        setShowLimitDialog(false);
    };

    const handleLimitDialogCancel = () => {
        setShowLimitDialog(false);
    };

    const removeClicked = async (id) => {
        await sourceEntity.removeLink(sourceEntityId, targetEntity, id);
        onChange();
    };

    const linkModalColumns = useMemo(() => {
        const nameCol = { ...targetEntity.columns['NameWithLogo'] };
        const originalRender = nameCol.render;
        nameCol.render = (text, record) => (
            <span className="flex items-center justify-between gap-3 w-full">
                {originalRender?.(record)}
                {record?.status && record.status !== 'ACTIVE' && (
                    <Tag color="orange" className="ml-auto shrink-0">{getStatusText(record.status, 'Player')}</Tag>
                )}
            </span>
        );
        return [
            nameCol,
            { ...targetEntity.columns['User'] },
        ];
    }, [targetEntity]);

    return (
        <>
            <RelationsTable
                data={data}
                sourceEntity={sourceEntity}
                targetEntity={targetEntity}
                emptyMessage={`This ${sourceEntity.displayName} is not associated with any ${targetEntity.displayNamePlural}`}
                addButton={{
                    label: hasExistingPlayer ? "Add Player (Limited)" : "Add Player",
                    icon: icons.Link,
                    onClick: handleAddClick,
                }}
                removeClicked={isSystemAdmin && removeClicked}
                columns={[
                    { ...targetEntity.columns['NameWithLogo'] },
                    { ...targetEntity.columns['Organization'] },
                    { ...targetEntity.columns['Teams'] },
                    { ...targetEntity.columns['Positions'] },
                    { ...targetEntity.columns['Jerseys'] },
                    { ...targetEntity.columns['status'] },
                    { ...targetEntity.columns['birthday'] },
                    { ...targetEntity.columns['email'] },
                    { ...targetEntity.columns['phone'] },
                ]}
            />

            <LinkRelationModal
                entity={targetEntity}
                open={linkRelation}
                onOk={linkRelationOk}
                onCancel={linkRelationCancel}
                columns={linkModalColumns}
                relations={['User(email)']}
                filters={{ status: ['PREAPPROVED', 'ACTIVE', 'INACTIVE', 'RETIRED', 'UNKNOWN', 'INVITED'] }}
                singleSelection={true}
            />

            <Confirm
                visible={showLimitDialog}
                title="Player Limit Reached"
                message="Only one player can be linked to a user. Please remove the existing player before adding a new one."
                confirmButtonText="OK"
                cancelButtonText={null}
                handleConfirm={handleLimitDialogConfirm}
                handleCancel={handleLimitDialogCancel}
            />
        </>
    )
}

export default UserPlayers
